import React, { useEffect, useState } from "react";
import { Row, Col, Badge } from "react-bootstrap";
import { LiveChat } from "./LiveChat";
import { useSocket } from "../../../../hooks/useSocket";
import { useAdminInfo } from "../../../../hooks/useAdminInfo";
import { Title } from "../../../../Templates/Title";
import { MdOutlineContactSupport } from "react-icons/md";
import { NonSelectedChat } from "../Chat/NonSelectedChat";
import { getAnonChats } from "../../Api/AnonChats/Chats";
import { SingleAnonChat } from "../../Api/AnonChats/models";
import { Home } from "../Home/Home";
import { IoEye, IoImageOutline } from "react-icons/io5";
import { IoEyeOff } from "react-icons/io5";

export const AnnonChat: React.FC = () => {
  const { socket } = useSocket();
  const { adminInfo } = useAdminInfo();
  const [chats, setChats] = useState<SingleAnonChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<SingleAnonChat | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showHome, setShowHome] = useState(true);
  const fetchChats = async (pageNumber: number) => {
    try {
      setLoading(true);
      const res = await getAnonChats({
        page: pageNumber,
        searchValue: searchValue
      });

      if (res?.chats) {
        if (pageNumber === 1) {
          setChats(res.chats);
        } else {
          setChats(prev => [...prev, ...res.chats]);
        }
        setHasMore(res.chats.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleSelectChat = (chat: SingleAnonChat) => {
    setSelectedChat(chat);
    if (chat.seenByUser) return;
    if (socket) {
      socket.emit(`seen-anon-chat`, {
        ChatId: chat.id,
        OwnerId: adminInfo?.id,
        role: 'user',
      });
    }

    // Update chat locally
    setChats(prev => prev.map(c => c.id === chat.id ? { ...c, seenByUser: true } : c));
  }

  useEffect(() => {
    fetchChats(page);
  }, [page]);

  useEffect(() => {
    if (searchValue === '') {
      setPage(1);
      fetchChats(1);
    }
  }, [searchValue]);

  useEffect(() => {
    if (socket) {
      socket.on(`update-anon-chats-${adminInfo?.id}`, (data: { message: any }) => {
        setPage(1);
        fetchChats(1);
      });
    }
    return () => {
      if (socket) {
        socket.off(`update-anon-chats-${adminInfo?.id}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, adminInfo]);

  return (
    <div className="mt-4 px-2 rounded text-light w-full pb-5" style={{ height: 'calc(100vh - 100px)' }}>

      <div className="d-flex justify-content-between align-items-end">
        <Title
          title="Soporte"
          icon={<MdOutlineContactSupport size={30} />}
          subtitle="Chatea con usuarios anónimos para brindar ayuda"
        />
        <div className="d-flex align-items-center mx-2">
          <span className="me-2 text-white">
            <Badge
              bg={showHome ? "secondary" : "primaryy"}
              onClick={() => setShowHome(!showHome)}
              role="button"
              className=" d-flex align-items-center gap-1"
            >
              {showHome ? "Ocultar panel" : "Mostrar panel"}
              {showHome ? <IoEyeOff size={15} /> : <IoEye size={15} />}
            </Badge>
          </span>
        </div>
      </div>
      <hr />

      <Row className="p-2 rounded-3">
        <Col md={2} className="my-0 d-flex flex-column">
          <div
            style={{ overflowY: 'auto', height: 'calc(95vh - 100px)' }}
            className="px-1"
            onScroll={handleScroll}
          >

            {chats?.map((chat, idx) => (
              <div key={idx} role='button'
                className={`position-relative rounded-3 mb-2 p-2 border border-secondary align-items-center ${selectedChat?.id === chat.id ? "bg-dark" : ""} ${chat?.seen ? "border-primaryy" : ""}`}
                onClick={() => { handleSelectChat(chat) }}>
                <div className="d-flex flex-column position-relative">
                  <span className="text-primaryy"><b>Anónimo</b></span>
                  <span>
                    {chat?.AnnonChatMessages[0]?.creatorType === 'player' ?
                      <span className="text-white">
                        {chat?.AnnonChatMessages[0]?.type === 'text' && chat?.AnnonChatMessages[0]?.message?.slice(0, 35) + (chat?.AnnonChatMessages[0]?.message?.length > 35 ? "..." : "")}
                        {chat?.AnnonChatMessages[0]?.type === 'image' &&
                          <div className="d-flex align-items-center gap-1">
                            <IoImageOutline size={15} /> Imagen
                          </div>
                        }
                      </span>
                      :
                      <span className="text-secondary">
                        {chat?.AnnonChatMessages[0]?.type === 'text' && `Tú: ${chat?.AnnonChatMessages[0]?.message?.slice(0, 35) + (chat?.AnnonChatMessages[0]?.message?.length > 35 ? "..." : "")}`}
                        {chat?.AnnonChatMessages[0]?.type === 'image' &&
                          <div className="d-flex align-items-center gap-1">
                           Tú: <IoImageOutline size={15} /> Imagen
                          </div>
                        }
                      </span>
                    }
                  </span>
                  <div className="d-flex justify-content-end position-absolute" style={{ top: 0, right: 0 }}>
                    <span className="text-secondary text-xs">{new Date(chat?.AnnonChatMessages[0]?.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
                {!chat.seenByUser && selectedChat?.id !== chat.id && <div className="position-absolute" style={{ bottom: '10px', right: '10px' }}>
                  <span className="text-white text-xs bg-danger rounded-circle d-flex align-items-center justify-content-center"
                    style={{ fontSize: '10px', width: '20px', height: '20px' }}>
                    1
                  </span>
                </div>
                }
              </div>
            ))}

            {!chats.length && !loading && <div className="text-center py-2 border border-secondary rounded-3">No tienes chats</div>}
            {loading && <div className="text-center py-2">Cargando...</div>}
          </div>
        </Col>
        <Col md={showHome ? 6 : 10}>
          <Row>
            {selectedChat && <LiveChat chat={selectedChat} />}
            {!selectedChat && <NonSelectedChat />}
          </Row>
        </Col>

        {showHome &&
          <Col md={4} style={{ overflowY: 'auto', height: 'calc(95vh - 100px)' }}>
            <Home embed={true} />
          </Col>
        }
      </Row>

    </div >
  );
};
